/**
 * VersionDiff - Component for displaying diff between two document versions
 *
 * Uses word-level diffing to highlight additions and removals between
 * the content of two versions.
 */
import { useMemo } from "react";
import * as Diff from "diff";
import { format } from "date-fns";
import { GitCompare, ArrowRight } from "lucide-react";
import type { DocumentVersionDetail } from "../../../types/version";

interface VersionDiffProps {
  /** Older version (base for comparison) */
  oldVersion: DocumentVersionDetail;
  /** Newer version (shows changes) */
  newVersion: DocumentVersionDetail;
}

export function VersionDiff({ oldVersion, newVersion }: VersionDiffProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  // Compute word-level diff between version contents
  const contentDiff = useMemo(() => {
    const oldText = oldVersion.content || "";
    const newText = newVersion.content || "";
    return Diff.diffWords(oldText, newText);
  }, [oldVersion.content, newVersion.content]);

  // Compute title diff if titles are different
  const titleDiff = useMemo(() => {
    if (oldVersion.title === newVersion.title) return null;
    return Diff.diffWords(oldVersion.title, newVersion.title);
  }, [oldVersion.title, newVersion.title]);

  // Count additions and removals
  const stats = useMemo(() => {
    const added = contentDiff.filter((p) => p.added).length;
    const removed = contentDiff.filter((p) => p.removed).length;
    return { added, removed };
  }, [contentDiff]);

  return (
    <div className="flex-1 p-6 bg-white overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-indigo-600 mb-3">
            <GitCompare className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Compare Versions</h2>
          </div>

          {/* Version comparison header */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Version {oldVersion.version_number}
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(oldVersion.created_at)}
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Version {newVersion.version_number}
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(newVersion.created_at)}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 bg-green-100 rounded border border-green-200" />
              <span className="text-gray-600">
                {stats.added} addition{stats.added !== 1 ? "s" : ""}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 bg-red-100 rounded border border-red-200" />
              <span className="text-gray-600">
                {stats.removed} removal{stats.removed !== 1 ? "s" : ""}
              </span>
            </span>
          </div>
        </div>

        {/* Title diff (if changed) */}
        {titleDiff && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Title Changes
            </h3>
            <div className="p-3 bg-gray-50 rounded-lg text-lg font-medium">
              {titleDiff.map((part, index) => {
                if (part.added) {
                  return (
                    <span
                      key={index}
                      className="bg-green-100 text-green-800 px-0.5 rounded"
                    >
                      {part.value}
                    </span>
                  );
                }
                if (part.removed) {
                  return (
                    <span
                      key={index}
                      className="bg-red-100 text-red-800 line-through px-0.5 rounded"
                    >
                      {part.value}
                    </span>
                  );
                }
                return <span key={index}>{part.value}</span>;
              })}
            </div>
          </div>
        )}

        {/* Content diff */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Content Changes
          </h3>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono">
              {contentDiff.map((part, index) => {
                if (part.added) {
                  return (
                    <span
                      key={index}
                      className="bg-green-100 text-green-800 px-0.5 rounded"
                    >
                      {part.value}
                    </span>
                  );
                }
                if (part.removed) {
                  return (
                    <span
                      key={index}
                      className="bg-red-100 text-red-800 line-through px-0.5 rounded"
                    >
                      {part.value}
                    </span>
                  );
                }
                return <span key={index}>{part.value}</span>;
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-green-100 rounded border border-green-200" />
            Added text
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-red-100 rounded border border-red-200" />
            <span className="line-through">Removed text</span>
          </span>
        </div>
      </div>
    </div>
  );
}
